import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface ClosetItem {
  id: string;
  user_id: string;
  name: string;
  type: string;
  color: string | null;
  mood: string | null;
  occasion: string | null;
  price: number;
  image_url: string | null;
  brand: string | null;
  created_at: string;
  updated_at: string;
}

export function useCloset() {
  const { user } = useAuth();
  const [items, setItems] = useState<ClosetItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('closet_items')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching closet items:', error);
      setLoading(false);
      return;
    }

    // No seeding - users start with empty closet and add their own items
    setItems(data as ClosetItem[]);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchItems();
    } else {
      setItems([]);
      setLoading(false);
    }
  }, [user, fetchItems]);

  const addItem = async (item: Omit<ClosetItem, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user) return null;

    const { data, error } = await supabase
      .from('closet_items')
      .insert({ ...item, user_id: user.id })
      .select()
      .single();

    if (error) {
      toast.error('Failed to add item');
      return null;
    }

    setItems(prev => [data as ClosetItem, ...prev]);
    toast.success('Added to wardrobe');
    return data as ClosetItem;
  };

  const updateItem = async (id: string, updates: Partial<ClosetItem>) => {
    const { error } = await supabase
      .from('closet_items')
      .update(updates)
      .eq('id', id);

    if (error) {
      toast.error('Failed to update item');
      return false;
    }

    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, ...updates } : item
    ));
    toast.success('Item updated');
    return true;
  };

  const deleteItem = async (id: string) => {
    const { error } = await supabase
      .from('closet_items')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error('Failed to delete item');
      return false;
    }

    setItems(prev => prev.filter(item => item.id !== id));
    toast.success('Item removed');
    return true;
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    if (!user) return null;

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('closet-images')
      .upload(fileName, file);

    if (uploadError) {
      toast.error('Failed to upload image');
      return null;
    }

    // Get signed URL for private bucket (1 year expiry for display)
    const { data, error: signedUrlError } = await supabase.storage
      .from('closet-images')
      .createSignedUrl(fileName, 31536000); // 1 year in seconds

    if (signedUrlError || !data?.signedUrl) {
      toast.error('Failed to get image URL');
      return null;
    }

    return data.signedUrl;
  };

  const closetValue = items.reduce((acc, item) => acc + (item.price || 0), 0);

  return {
    items,
    loading,
    addItem,
    updateItem,
    deleteItem,
    uploadImage,
    closetValue,
    refetch: fetchItems,
  };
}
